import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X } from "lucide-react";
import { isMaintenanceMode } from "@/utils/maintenanceMode";

const API = `${process.env.REACT_APP_API_URL}/api`;
const MAINTENANCE_ANNOUNCEMENT = {
  id: "maintenance-prelaunch",
  message:
    "🚀 Veriksa segera hadir! Kami sedang menyiapkan platform untuk kamu. Pantau terus perkembangannya.",
  theme: "info",
  dismissible: false,
  cta_text: null,
  cta_url: null,
};

const THEME_CLASS = {
  info: "bg-sky-100 border-sky-300 text-sky-900 dark:bg-sky-900 dark:border-sky-700 dark:text-sky-100",
  success: "bg-emerald-100 border-emerald-300 text-emerald-900 dark:bg-emerald-900 dark:border-emerald-700 dark:text-emerald-100",
  warning: "bg-amber-100 border-amber-300 text-amber-900 dark:bg-amber-900 dark:border-amber-700 dark:text-amber-100",
  danger: "bg-red-100 border-red-300 text-red-900 dark:bg-red-900 dark:border-red-700 dark:text-red-100",
  promo: "bg-violet-100 border-violet-300 text-violet-900 dark:bg-violet-900 dark:border-violet-700 dark:text-violet-100",
};

export default function AnnouncementBar() {
  const [announcement, setAnnouncement] = useState(null);
  const [hidden, setHidden] = useState(false);
  const maintenanceMode = isMaintenanceMode();

  useEffect(() => {
    if (maintenanceMode) {
      setAnnouncement(MAINTENANCE_ANNOUNCEMENT);
      return () => {};
    }

    let mounted = true;

    const load = async () => {
      try {
        const res = await axios.get(`${API}/announcements/active`, {
          withCredentials: true,
        });
        if (!mounted) return;
        const data = res.data || null;
        setAnnouncement(data);
      } catch {
        if (!mounted) return;
        setAnnouncement(null);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [maintenanceMode]);

  useEffect(() => {
    if (!announcement?.id) {
      setHidden(false);
      return;
    }
    const dismissed = localStorage.getItem(`veriksa_announcement_dismissed_${announcement.id}`);
    setHidden(Boolean(announcement.dismissible && dismissed));
  }, [announcement?.id, announcement?.dismissible]);

  const themeClass = useMemo(() => {
    return THEME_CLASS[announcement?.theme] || THEME_CLASS.info;
  }, [announcement?.theme]);

  const handleDismiss = () => {
    if (announcement?.id) {
      localStorage.setItem(`veriksa_announcement_dismissed_${announcement.id}`, "1");
    }
    setHidden(true);
  };

  if (!announcement || hidden) return null;

  const ctaUrl = String(announcement.cta_url || "");
  const isExternal = /^https?:\/\//i.test(ctaUrl);

  return (
    <div className={`w-full border-b ${themeClass}`}>
      <div className="relative max-w-7xl mx-auto h-10 px-4 md:px-8 flex items-center justify-center">
        <div className={`min-w-0 flex items-center gap-3 text-sm ${announcement.dismissible ? "pr-8" : ""}`}>
          <span className="truncate">{announcement.message}</span>
          {announcement.cta_text && announcement.cta_url && (
            isExternal ? (
              <a
                href={ctaUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 text-xs font-semibold px-2 py-1 rounded border border-current/40 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              >
                {announcement.cta_text}
              </a>
            ) : (
              <Link
                to={ctaUrl}
                className="shrink-0 text-xs font-semibold px-2 py-1 rounded border border-current/40 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              >
                {announcement.cta_text}
              </Link>
            )
          )}
        </div>
        {announcement.dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
            aria-label="Tutup pengumuman"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
